import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  styled,
  useTheme,
  Typography,
} from "@mui/material";
import closeIcon from "../../assets/ic_alert_close.svg";
import CommonButton from "../CommonButton";
import { useTranslation } from "react-i18next";

interface DailyRemarkDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  initialNotes: string | null;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  noteText: string | null;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "400px",
    width: "90%",
    margin: "16px",
    borderRadius: "10px",
    position: "relative", // Add this to ensure absolute positioning works correctly
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "17px",
    backgroundColor: theme.palette.secondary.main,
    fontFamily: "Nunito Sans Regular500",
    mt: "10px",
    "& fieldset": {
      BorderColor: theme.palette.secondary.dark,
    },
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "16px",
  top: "16px",
  zIndex: 1,
}));

const DailyRemarkDialog: React.FC<DailyRemarkDialogProps> = ({
  open,
  onClose,
  onSave,
  initialNotes,
  onChange,
  noteText
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <StyledDialog open={open} onClose={onClose}>
      <CloseButton
        aria-label="close"
        onClick={onClose}
        sx={{ color: "text.secondary" }}
      >
        <img
          src={closeIcon}
          alt={`close-icon`}
          width="16px"
          height="16px"
        />
      </CloseButton>

      <DialogContent sx={{ pt: 4 }}>
        <Box
          sx={{
            minHeight: 50,
            display: "flex",
            justifyContent: "left",
            alignItems: "left",
            pb: "10px",
          }}
        >
          <Typography
            fontFamily={"Nunito Sans Regular500"}
            sx={{
              textAlign: "left",
              justifyContent: "left",
              maxWidth: "320px",
            }}
          >
            {noteText
              ? noteText
              : t("taskOverViewScreen.noNotesAvailable")}
          </Typography>
        </Box>
        <StyledTextField
          autoFocus
          id="notes"
          multiline
          rows={3}
          fullWidth
          variant="outlined"
          value={initialNotes ?? ""}
          onChange={onChange}
          sx={{ mt: "16px" }}
          placeholder=""
          disabled={!noteText}
        />
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "flex-end",
          mr: "30px",
          pb: 1,
        }}
      >
        <CommonButton
          translationKey={"common.button_opslaan"}
          type="submit"
          onClick={onSave}
          sx={{
            borderRadius: "6px",
            width: "auto",
            padding: "8px 8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.secondary.main,
            fontFamily: "Nunito Sans Regular500",
            fontWeight: 700,
          }}
          disabled={!noteText}
        />
      </DialogActions>
    </StyledDialog>
  );
};

export default DailyRemarkDialog;
